import React, {useState} from 'react'
import classnames from "classnames";
import {Link} from "react-router-dom";

import Popover from "@mui/material/Popover";
import MoreVertIcon from '@mui/icons-material/MoreVert';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import DeleteSharpIcon from '@mui/icons-material/DeleteSharp';
import LocationOnIcon from "@mui/icons-material/LocationOn";
import EditLocationIcon from '@mui/icons-material/EditLocation';
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks';

import CustomCurrencyInput from "../../../components/CustomCurrencyInput";
import {useThemeStore} from "../../../stores/themeStore";
import {usePricingRow} from "../usePricingRow";
import {PriceAdjustSignToggle} from "../PriceAdjustSignToggle";

// The five value columns shown inline on each pricing row (mirrors the desktop
// table columns). Exported so the trim header can label the same columns.
// `cell` picks the renderer: most are plain money boxes, but Price Adjust is a
// signed control and % off MSRP is a percentage, so they draw their own.
export const PRICING_MONEY_FIELDS = [
    {key: 'msrp', label: 'MSRP'},
    {key: 'msrp_percentage', label: '% off MSRP', cell: 'percent'},
    {key: 'invoice', label: 'Invoice', allowNull: true},
    {key: 'price_adjust', label: 'Price Adjust', cell: 'priceAdjust'},
    {key: 'profit', label: 'Profit', defaultValue: 0},
]

// Compact currency box for the row (no $ prefix — the column header labels it).
// inputMode="decimal" makes phones pop the numeric keypad instead of the full
// alphanumeric keyboard.
const RowMoney = ( { id, value, onChange, allowNull, allowBlank, allowNegative, defaultValue } ) => (
    <div className="pim-money pim-rowmoney">
        <CustomCurrencyInput
            id={id}
            value={value}
            onChange={onChange}
            placeholder={'0'}
            allowNull={allowNull}
            allowBlank={allowBlank}
            allowNegative={allowNegative}
            defaultValue={defaultValue}
            InputProps={{}}
            inputProps={{ inputMode: 'decimal' }}
        />
    </div>
)

// % off MSRP reads as a percentage, so it carries a trailing % instead of the
// column's implied dollars. The − sits outside the box (a fixed label, not a
// toggle — the column only ever discounts), mirroring the desktop cell.
const MsrpPercentageCell = ( { id, value, onChange } ) => (
    <div className="pim-percent-control">
        <span className="pim-percent-op">−</span>
        <div className="pim-money pim-rowmoney pim-percent">
            <CustomCurrencyInput
                id={id}
                value={value}
                onChange={onChange}
                placeholder={'0'}
                allowBlank={true}
                allowNegative={false}
                InputProps={{}}
                inputProps={{ inputMode: 'decimal' }}
            />
            <span className="pim-percent__suffix">%</span>
        </div>
    </div>
)

// Price Adjust is a signed control rather than a plain money box: the +/-
// selector sits immediately left of an unsigned magnitude.
const PriceAdjustCell = ( { priceAdjust, onSignChange, onMagnitudeChange, buildId } ) => (
    <div className="pim-price-adjust">
        <PriceAdjustSignToggle
            sign={priceAdjust.sign}
            onChange={onSignChange}
            buildId={buildId}
        />
        <RowMoney
            id={buildId('price_adjust')}
            value={priceAdjust.magnitude}
            onChange={onMagnitudeChange}
            allowBlank={true}
            allowNegative={false}
        />
    </div>
)

// Mobile presentation of a single pricing — same behavior as the desktop
// `PricingListRow` (both consume `usePricingRow`), laid out as one compact row:
// the five money fields inline, notes + actions behind a ⋮ menu. When `pricing`
// is undefined this is the "add a new pricing" row for the trim.
export const PricingListRowMobile = ( { trim, pricing } ) => {
    const theme = useThemeStore((state) => state.theme)
    const [anchorEl, setAnchorEl] = useState(null)

    const {
        pricingState,
        updatePricingState,
        priceAdjust,
        setPriceAdjustSign,
        setPriceAdjustMagnitude,
        msrpPercentage,
        setMsrpPercentage,
        isDefaultLocation,
        deletePricingRow,
        deskPricing,
        openLocationModal,
        buildId,
        isDisabled,
        isCreatingLease,
        isCreatingPurchase,
    } = usePricingRow({trim, pricing})

    const isNew = !pricing?.id

    const openMenu = (e) => { e.stopPropagation(); setAnchorEl(e.currentTarget) }
    const closeMenu = () => setAnchorEl(null)

    const desk = (isPurchase) => { closeMenu(); deskPricing(isPurchase) }
    const editLocation = () => { closeMenu(); openLocationModal() }
    const remove = () => { closeMenu(); deletePricingRow(pricing.id) }

    return (
        <div className={classnames('pim-prow', {'pim-prow--new': isNew})}>
            {
                isNew &&
                <span className="pim-prow__new-label">
                    <AddCircleOutlineIcon/> New pricing
                </span>
            }
            {PRICING_MONEY_FIELDS.map((field) => {
                if (field.cell === 'priceAdjust') return (
                    <PriceAdjustCell
                        key={field.key}
                        priceAdjust={priceAdjust}
                        onSignChange={setPriceAdjustSign}
                        onMagnitudeChange={setPriceAdjustMagnitude}
                        buildId={buildId}
                    />
                )
                if (field.cell === 'percent') return (
                    <MsrpPercentageCell
                        key={field.key}
                        id={buildId(field.key)}
                        value={msrpPercentage}
                        onChange={setMsrpPercentage}
                    />
                )
                return (
                    <RowMoney
                        key={field.key}
                        id={buildId(field.key)}
                        value={pricingState[field.key]}
                        onChange={(value) => updatePricingState(field.key, value)}
                        allowNull={field.allowNull}
                        defaultValue={field.defaultValue}
                    />
                )
            })}

            <button
                type="button"
                className="pim-prow__menu-btn"
                aria-label="Pricing actions"
                onClick={openMenu}
            >
                <MoreVertIcon/>
            </button>

            <Popover
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                onClose={closeMenu}
                onClick={(e) => e.stopPropagation()}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                slotProps={{ paper: { className: 'pim-menu-paper', 'data-theme': theme } }}
            >
                <div className="pim-menu">
                    <label className="pim-field">
                        <span className="pim-field__label">Notes</span>
                        <input
                            className="pim-menu__notes"
                            placeholder={'Add notes'}
                            value={pricingState.description || ''}
                            onChange={(e) => updatePricingState('description', e.target.value)}
                        />
                    </label>

                    <div className="pim-menu__actions">
                        <button
                            type="button"
                            className={classnames('pim-action pim-action--lease', {
                                loading: isCreatingLease, disabled: isDisabled,
                            })}
                            disabled={isDisabled}
                            onClick={() => desk(false)}
                        >
                            <AddOutlinedIcon/><span>Lease</span>
                        </button>
                        <button
                            type="button"
                            className={classnames('pim-action pim-action--purchase', {
                                loading: isCreatingPurchase, disabled: isDisabled,
                            })}
                            disabled={isDisabled}
                            onClick={() => desk(true)}
                        >
                            <AccountBalanceIcon/><span>Purchase</span>
                        </button>
                        <button
                            type="button"
                            className="pim-action pim-action--ghost"
                            onClick={editLocation}
                        >
                            {isDefaultLocation ? <LocationOnIcon/> : <EditLocationIcon/>}
                            <span>Location</span>
                        </button>
                        {
                            !isNew &&
                            <Link
                                to={`/quotes?pricing_id=${pricing.id}`}
                                target="_blank"
                                className="pim-action pim-action--ghost"
                                onClick={closeMenu}
                            >
                                <LibraryBooksIcon/><span>Quotes</span>
                            </Link>
                        }
                        {
                            !isNew &&
                            <button
                                type="button"
                                className="pim-action pim-action--danger"
                                onClick={remove}
                            >
                                <DeleteSharpIcon/><span>Delete</span>
                            </button>
                        }
                    </div>
                </div>
            </Popover>
        </div>
    )
}
